import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "input", "messages", "submitButton"]
  
  connect() {
    this.scrollToBottom()
  }
  
  async submit(event) {
    event.preventDefault()
    
    const message = this.inputTarget.value.trim()
    if (!message) return
    
    // Disable form and show loading state
    this.setLoadingState(true)
    
    // Add user message immediately
    this.addUserMessage(message)
    
    // Show typing indicator
    const typingIndicator = this.addTypingIndicator()
    
    try {
      // Create form data manually to ensure message is included
      const formData = new FormData()
      formData.append('message', message)
      formData.append('authenticity_token', document.querySelector('input[name="authenticity_token"]').value)
      
      console.log('Submitting to:', this.formTarget.action)
      console.log('Form data:', Object.fromEntries(formData))
      
      // Clear input after getting the value
      this.inputTarget.value = ""
      
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      
      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers))
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('HTTP Error Response:', errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const responseText = await response.text()
      console.log('Raw response:', responseText)
      
      let data
      try {
        data = JSON.parse(responseText)
        console.log('Parsed data:', data)
      } catch (parseError) {
        console.error('JSON parse error:', parseError)
        console.error('Response text was:', responseText)
        throw new Error('Invalid JSON response from server')
      }
      
      // Remove typing indicator and clear timer
      this.removeTypingIndicator(typingIndicator)
      
      if (data.success && data.messages) {
        // Add AI response (should be the last message)
        const aiMessage = data.messages.find(msg => msg.role === 'assistant')
        if (aiMessage) {
          this.addAiMessage(aiMessage)
        }
      } else {
        this.addErrorMessage("Sorry, I couldn't process your message. Please try again.")
      }
      
    } catch (error) {
      console.error('Chat error:', error)
      this.removeTypingIndicator(typingIndicator)
      this.addErrorMessage("Connection error. Please check your internet and try again.")
    } finally {
      this.setLoadingState(false)
      this.inputTarget.focus()
    }
  }
  
  addUserMessage(content) {
    const userName = this.element.dataset.currentUserName || 'User'
    const userInitial = userName.charAt(0).toUpperCase()
    
    const messageHtml = `
      <div class="flex items-end space-x-3 mb-4 flex-row-reverse space-x-reverse">
        <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-500">
          <span class="text-white text-sm font-medium">${userInitial}</span>
        </div>
        <div class="relative max-w-md ml-12">
          <div class="bg-blue-500 text-white rounded-t-2xl rounded-bl-2xl rounded-br-md px-4 py-3">
            <p class="text-sm leading-relaxed break-words">${this.escapeHtml(content)}</p>
          </div>
          <div class="flex justify-end mt-1">
            <span class="text-xs text-gray-500 px-2">${this.getCurrentTime()}</span>
          </div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    this.scrollToBottom()
  }
  
  addAiMessage(message) {
    // Escape the content for the speak button
    const speakContent = (message.content || '').replace(/'/g, "\\'").replace(/"/g, '\\"')
    const messageId = `ai-message-${Date.now()}`
    
    const messageHtml = `
      <div class="flex items-end space-x-3 mb-4 ai-message" id="${messageId}">
        <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
          <i class="fas fa-brain text-white text-sm"></i>
        </div>
        <div class="relative max-w-md mr-12">
          <div class="bg-gray-100 text-gray-800 rounded-t-2xl rounded-br-2xl rounded-bl-md px-4 py-3">
            <div class="prose prose-sm max-w-none text-gray-800">${message.formatted_content || message.content}</div>
          </div>
          <div class="flex justify-between mt-1">
            <span class="text-xs text-gray-500 px-2">${message.timestamp || this.getCurrentTime()}</span>
            <button onclick="speakText('${speakContent}', this)" 
                    class="text-xs text-gray-400 hover:text-blue-600 px-2 flex items-center transition-colors"
                    title="Read aloud"
                    data-speaking="false">
              <i class="fas fa-volume-up mr-1"></i>
              <span class="hidden sm:inline">Speak</span>
            </button>
          </div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
    
    // Scroll to the top of the new AI message with some offset
    setTimeout(() => {
      const newMessage = document.getElementById(messageId)
      if (newMessage) {
        // Get the scrollable container
        const scrollContainer = document.querySelector('.flex-1.overflow-y-auto')
        if (scrollContainer) {
          // Calculate position to scroll to (top of message minus some padding)
          const messageTop = newMessage.offsetTop - 20 // 20px padding from top
          scrollContainer.scrollTop = messageTop
        }
      }
    }, 100)
  }
  
  addTypingIndicator() {
    const thinkingTexts = [
      "Thinking",
      "Processing", 
      "Analyzing",
      "Computing",
      "Considering", 
      "Pondering",
      "Calculating",
      "Evaluating",
      "Working on it",
      "Let me think"
    ]
    
    const randomText = thinkingTexts[Math.floor(Math.random() * thinkingTexts.length)]
    
    const typingHtml = `
      <div class="flex items-end space-x-3 mb-4 typing-indicator">
        <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
          <i class="fas fa-brain text-white text-sm"></i>
        </div>
        <div class="bg-gray-100 text-gray-800 rounded-t-2xl rounded-br-2xl rounded-bl-md px-4 py-3 max-w-md mr-12">
          <div class="flex items-center space-x-2">
            <span class="thinking-text text-sm text-gray-600">${randomText}</span>
            <div class="flex items-center space-x-1">
              <div class="typing-dot"></div>
              <div class="typing-dot"></div>
              <div class="typing-dot"></div>
            </div>
          </div>
          <div class="timer-text text-xs text-gray-500 mt-1">0s</div>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', typingHtml)
    this.scrollToBottom()
    
    const indicator = this.messagesTarget.querySelector('.typing-indicator')
    const startTime = Date.now()
    
    // Start timer
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      const timerElement = indicator.querySelector('.timer-text')
      if (timerElement) {
        timerElement.textContent = `${elapsed}s`
      }
    }, 1000)
    
    // Store timer ID so we can clear it later
    indicator.dataset.timerId = timer
    
    return indicator
  }
  
  removeTypingIndicator(indicator) {
    if (indicator) {
      // Clear the timer
      const timerId = indicator.dataset.timerId
      if (timerId) {
        clearInterval(parseInt(timerId))
      }
      // Remove the element
      indicator.remove()
    }
  }
  
  addErrorMessage(content) {
    const errorHtml = `
      <div class="flex items-start space-x-3 mb-4">
        <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-red-500">
          <i class="fas fa-exclamation-triangle text-white text-sm"></i>
        </div>
        <div class="bg-red-100 text-red-800 rounded-lg p-3 max-w-md">
          <p>${content}</p>
          <span class="text-xs opacity-75 mt-1 block">${this.getCurrentTime()}</span>
        </div>
      </div>
    `
    this.messagesTarget.insertAdjacentHTML('beforeend', errorHtml)
    this.scrollToBottom()
  }
  
  setLoadingState(loading) {
    this.inputTarget.disabled = loading
    this.submitButtonTarget.disabled = loading
    
    if (loading) {
      this.submitButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Sending...'
    } else {
      this.submitButtonTarget.innerHTML = 'Send'
    }
  }
  
  scrollToBottom() {
    setTimeout(() => {
      // Find the scrollable container (the one with overflow-y-auto)
      const scrollContainer = document.querySelector('.flex-1.overflow-y-auto')
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight
      }
    }, 100)
  }
  
  
  getCurrentTime() {
    return new Date().toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  }
  
  getUserInitial() {
    const userName = document.body.dataset.currentUserName || 'U'
    return userName.charAt(0).toUpperCase()
  }
  
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}